import React, { useState, useEffect } from 'react';
import { Settings, Bell, BellOff, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './NotificationSettings.css';

const NotificationSettings = () => {
  const { user, isAdmin } = useAuth();
  const [settings, setSettings] = useState({
    attestationNotifications: true,
    leaveNotifications: true,
    systemNotifications: true,
    emailNotifications: true,
    adminNotifications: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const isAdminOrManager = isAdmin() || user?.role === 'manager';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications/settings');
      setSettings({
        attestationNotifications: response.attestationNotifications,
        leaveNotifications: response.leaveNotifications,
        systemNotifications: response.systemNotifications,
        emailNotifications: response.emailNotifications,
        adminNotifications: response.adminNotifications,
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Error loading notification settings.' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await api.patch('/notifications/settings', settings);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Error saving settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="notification-settings-page">
        <div className="page-loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="notification-settings-page">
      <div className="page-header">
        <h1><Settings size={24} /> Notification Settings</h1>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-container">
        <div className="settings-section">
          <h2>Notification Preferences</h2>
          <p className="section-description">
            Choose which notifications you want to receive. You can turn them on or off at any time.
          </p>

          <div className="settings-list">
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">
                  {settings.attestationNotifications ? <Bell size={20} /> : <BellOff size={20} />}
                </div>
                <div className="setting-content">
                  <h3>Attestation Notifications</h3>
                  <p>Get notified when your attestation requests are approved, rejected, or generated.</p>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.attestationNotifications}
                  onChange={() => handleToggle('attestationNotifications')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">
                  {settings.leaveNotifications ? <Bell size={20} /> : <BellOff size={20} />}
                </div>
                <div className="setting-content">
                  <h3>Leave Notifications</h3>
                  <p>Get notified about your leave request status updates.</p>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.leaveNotifications}
                  onChange={() => handleToggle('leaveNotifications')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">
                  {settings.systemNotifications ? <Bell size={20} /> : <BellOff size={20} />}
                </div>
                <div className="setting-content">
                  <h3>System Notifications</h3>
                  <p>Receive important system announcements and updates.</p>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.systemNotifications}
                  onChange={() => handleToggle('systemNotifications')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">
                  {settings.emailNotifications ? <Bell size={20} /> : <BellOff size={20} />}
                </div>
                <div className="setting-content">
                  <h3>Email Notifications</h3>
                  <p>Receive notifications via email (coming soon).</p>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={() => handleToggle('emailNotifications')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {isAdminOrManager && (
              <div className="setting-item">
                <div className="setting-info">
                  <div className="setting-icon">
                    {settings.adminNotifications ? <Bell size={20} /> : <BellOff size={20} />}
                  </div>
                  <div className="setting-content">
                    <h3>Admin/Manager Notifications</h3>
                    <p>Receive notifications about new requests that require your approval.</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.adminNotifications}
                    onChange={() => handleToggle('adminNotifications')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="settings-actions">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
